import { Component, inject, signal } from '@angular/core';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { Helpers } from '../../../../../utilities/helpers';
import { IStudent, IStudents } from '../../../../../interfaces/student';
import { FlashService } from '../../../../../services/flash';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { StudentService } from '../../../../../services/student';
import { Session } from '../../../../../services/session';
import { SchoolService } from '../../../../../services/school';
import { ISchools } from '../../../../../interfaces/school';
import { NgClass } from '@angular/common';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';

@Component({
  selector: 'app-students',
  imports: [
    ActionButton,
    LoadingIndicator,
    NotFoundError,
    Pagination,
    NgClass,
    ReactiveFormsModule,
    FormsModule,
    RouterLink,
  ],
  templateUrl: './students.html',
  styleUrl: './students.css',
})
export class Students {
  constructor(
    private studentService: StudentService,
    private session: Session,
    private schoolService: SchoolService,
    private router: Router,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole === 'SUPER_ADMIN') {
      this.getSchools();
    }

    this.getStudents();
  }

  protected students = signal<IStudents | null>(null);
  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected isFiltering = signal<boolean>(false);
  protected pagination = signal({ page: 0, size: 10 });
  protected readonly flashService = inject(FlashService);
  protected readonly Helpers = Helpers;
  protected filterSchoolId: string = '';
  protected schoolId?: string;
  protected loggedUserRole?: string;
  protected schools = signal<ISchools | null>(null);
  protected status: string = '';
  protected search: string = '';
  protected sex: string = '';
  protected changeStatus = signal<boolean>(false);
  protected validStatuses: string[] = [
    'ACTIVE',
    'SUSPENDED',
    'GRADUATED',
    'TRANSFERRED',
    'WITHDRAWN',
  ];
  protected selectedStudent!: IStudent['payload'];
  protected newStatus: string = '';
  protected isEditing = signal<boolean>(false);
  protected parentName!: string;
  protected parentPhone!: string;
  protected parentEmail!: string;
  protected dob!: string;
  protected admissionNumber!: string;
  protected firstName!: string;
  protected lastName!: string;
  protected admissionDate!: string;
  protected showEditStudent = signal<boolean>(false);
  protected todayDate = new Date().toISOString().split('T')[0];
  protected emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  protected admins = ['SUPER_ADMIN', 'SCHOOL_ADMIN'];

  protected getStudents(
    filterType = this.currentFilterType,
    value = this.currentFilterValue,
  ): void {
    this.isFiltering.set(true);

    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.studentService.getStudents(`${ApiEndpoints.url}/student`, params).subscribe({
      next: (data: IStudents) => {
        this.students.set(data);
        this.isFiltering.set(false);
      },
      error: (err) => {
        this.isFiltering.set(false);
        this.flashService.failure(
          err?.error?.message ?? 'Could not load students. Refresh the page',
        );
      },
    });
  }

  protected getSchools(): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getStudents();
  }

  protected selectStudent(action: string, payload: IStudent['payload']): void {
    if (action === 'status') {
      this.changeStatus.set(true);
    } else {
      this.showEditStudent.set(true);
    }

    this.selectedStudent = payload;
    this.parentName = this.selectedStudent.parent_name;
    this.parentPhone = this.selectedStudent.parent_phone;
    this.parentEmail = this.selectedStudent.parent_email;
    this.dob = new Date(this.selectedStudent.date_of_birth).toISOString().split('T')[0];
    this.admissionNumber = this.selectedStudent.admission_number;
    this.firstName = this.selectedStudent.first_name;
    this.lastName = this.selectedStudent.last_name;
    this.admissionDate = new Date(this.selectedStudent.admission_date).toISOString().split('T')[0];
  }

  protected editStudent(form: NgForm, action: 'status' | 'edit'): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (this.parentEmail && !this.emailPattern.test(this.parentEmail)) {
      this.flashService.failure('Enter a valid email address.');
      return;
    }

    let payload: Record<string, any> = {};

    if (action === 'status') {
      payload = {
        status: this.newStatus,
      };
    }

    if (action === 'edit') {
      const original = this.selectedStudent;

      if (this.dob !== original.date_of_birth) {
        payload['date_of_birth'] = this.dob;
      }

      if (this.admissionDate !== original.admission_date) {
        payload['admission_date'] = this.admissionDate;
      }

      if (this.firstName.trim() !== (original.first_name?.trim() || '')) {
        payload['first_name'] = this.firstName.trim();
      }

      if (this.lastName.trim() !== (original.last_name?.trim() || '')) {
        payload['last_name'] = this.lastName.trim();
      }

      if (this.admissionNumber.trim() !== (original.admission_number?.trim() || '')) {
        payload['admission_number'] = this.admissionNumber.trim();
      }

      if (this.parentName.trim() !== (original.parent_name?.trim() || '')) {
        payload['parent_name'] = this.parentName.trim();
      }

      if (this.parentPhone.trim() !== (original.parent_phone?.trim() || '')) {
        payload['parent_phone'] = this.parentPhone.trim();
      }

      if (this.parentEmail.trim() !== (original.parent_email?.trim() || '')) {
        payload['parent_email'] = this.parentEmail.trim();
      }
    }

    this.studentService
      .editStudent(`${ApiEndpoints.url}/student/${this.selectedStudent.id}/edit`, payload)
      .subscribe({
        next: () => {
          this.flashService.success('Successfully updated student.');
          this.isEditing.set(false);
          this.changeStatus.set(false);
          this.showEditStudent.set(false);
          this.newStatus = '';
          this.getStudents();
        },
        error: (err) => {
          this.isEditing.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Error updating student details. Try again.',
          );
        },
      });
  }

  protected navigateToStudentDetail(id: string): void {
    void this.router.navigate(['/dashboard/academic-management/student-details'], {
      queryParams: {
        id: id,
      },
    });
  }
}
